import { IconButton, makeStyles, Modal, Theme, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { PrimaryButton, SecondaryButton } from './Buttons';

const useStyles = makeStyles<
  Theme,
  {
    height?: number;
    width: number;
    isSmall: boolean;
    headerDivider: boolean;
    smallHeader?: boolean;
  }
>((theme: Theme) => ({
  modal: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: 16,
  },
  modalContent: ({ height, width, isSmall }) => ({
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    height: height ? height : 'auto',
    maxHeight: '100%',
    overflowY: 'scroll',
    maxWidth: 800,
    outline: 'none',
    width,

    [theme.breakpoints.down('sm')]: {
      height: isSmall ? '50%' : '100%',
      width: '100%',
    },
  }),
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  header: ({ headerDivider }) => ({
    alignItems: 'center',
    borderBottom: headerDivider ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
    display: 'flex',
    fontWeight: 500,
    justifyContent: 'space-between',
    padding: headerDivider ? theme.spacing(2) : '16px 16px 0 16px',
  }),
  title: ({ isSmall, smallHeader }) => ({
    fontSize: isSmall || smallHeader ? 16 : 24,
  }),
  footer: ({ isSmall }) => ({
    alignItems: 'center',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      flexDirection: isSmall ? 'row' : 'column',
      gap: theme.spacing(1),

      '& > div': {
        width: '100%',
      },
    },
  }),
  closeButton: {
    padding: 0,
  },
  primaryButton: {
    padding: '8px 22px',
    marginLeft: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: 0,
      marginTop: theme.spacing(1),
    },
  },
  secondaryButton: {
    padding: '8px 22px',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  tertiaryButton: {
    padding: '8px 22px',
    marginLeft: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: 0,
      marginTop: theme.spacing(1),
    },
  },
}));

interface MainModalProps {
  buttonTestId?: string;
  children: React.ReactNode;
  disabledPrimaryButton?: boolean;
  disabledSecondaryButton?: boolean;
  disabledTertiaryButton?: boolean;
  height?: number;
  isOpen: boolean;
  isSmall?: boolean;
  message?: string | string[];
  onClose: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  onTertiaryAction?: () => void;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  tertiaryButtonLabel?: string;
  testId?: string;
  title: string;
  width: number;
  headerDivider?: boolean;
  smallHeader?: boolean;
}

const MainModal = ({
  buttonTestId,
  children,
  disabledPrimaryButton,
  disabledSecondaryButton,
  disabledTertiaryButton,
  height,
  isOpen,
  isSmall = false,
  message,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
  onTertiaryAction,
  primaryButtonLabel,
  secondaryButtonLabel,
  tertiaryButtonLabel,
  testId,
  title,
  width,
  headerDivider = true,
  smallHeader = false,
}: MainModalProps) => {
  const classes = useStyles({ height, width, isSmall, headerDivider, smallHeader });

  return (
    <Modal className={classes.modal} data-testid={testId} onClose={onClose} open={isOpen}>
      <div className={classes.modalContent}>
        <div className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          <IconButton className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.content}>{children}</div>
        <div className={classes.footer}>
          <Typography>{message}</Typography>
          <div>
            {secondaryButtonLabel && (
              <SecondaryButton
                className={classes.secondaryButton}
                onClick={onSecondaryAction}
                disabled={!onSecondaryAction || disabledSecondaryButton}
              >
                {secondaryButtonLabel}
              </SecondaryButton>
            )}
            {tertiaryButtonLabel && (
              <PrimaryButton
                className={classes.tertiaryButton}
                onClick={onTertiaryAction}
                disabled={!onTertiaryAction || disabledTertiaryButton}
                data-testid={buttonTestId ? `${buttonTestId}-tertiary` : undefined}
              >
                {tertiaryButtonLabel}
              </PrimaryButton>
            )}
            {primaryButtonLabel && (
              <PrimaryButton
                className={classes.primaryButton}
                onClick={onPrimaryAction}
                disabled={!onPrimaryAction || disabledPrimaryButton}
                data-testid={buttonTestId}
              >
                {primaryButtonLabel}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MainModal;
